import type { TableColumnsType } from 'antd';
import { Button, message as messageApi, Modal, Select, Table } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { formatTimestampToDateTime } from 'utils/dateTimeUtils';
import { showError } from 'utils/exceptionUtils';
import { formatWeightKgToLb } from 'utils/numberUtils';
import Loading from "../../../components/Loading";
import TablePaginationBar from "../../../components/TablePaginationBar";
import TaskDetailDrawer from '../consolidation/components/TaskDetailDrawer';
import { useWarehouseContext } from "../../../contexts/WarehouseContext";
import BagDetailDrawer from './BagDetailDrawer';
import FilterArea, {
    bagStatusOptions,
    bagTypeOptions,
    DEFAULT_FILTER_OPTIONS,
    FilterOptions,
    getBagStatusOptions
} from './components/FilterArea';
import NewBagUnifiedModal from './components/NewBagUnifiedModal';
import PrintTagsModal from './components/PrintTagsModal';
import PrintTTTagsModal from './components/PrintTTTagsModal';
import { useDiscardSelectedTags, useReopenSelectedTags } from './graphql/mutations/batchOperations';
import { BagFiltersType } from './graphql/queries/getBagListing';
import {
    BagType,
    BagStatus,
    BusinessType,
    ExternalBusinessType,
    statusToText,
    getBagStatusText,
    getBagSourceTypeText,
    getBusinessTypeText,
    getExternalBusinessTypeText,
    getPrintTypeText,
    isTTBusinessType,
} from './graphql/types/BagTagType';
import {
    LIST_ACTIVE_BAG_CONTAINERS,
    ListActiveBagContainersResponse,
    BagContainerOperateType,
} from './graphql/queries/listActiveBagContainers';
import {
    UPDATE_BAG_CONTAINER,
    UpdateBagContainerResponse,
    UpdateBagContainerInput,
} from './graphql/mutations/updateBagContainer';
import { TTBag } from './graphql/mutations/createTTBag';
import { useBagQuery } from './hooks/useBagQuery';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { EXPORT_BAG_TAGS, ExportBagTagsResponse, ExportBagTagsVariables } from './graphql/mutations/exportBagTags';
import WmsDownloadModal from '../../../components/WmsDownloadModal';
import { GET_ALL_CARRIER_OPTIONS, GetAllCarrierOptionsResponse } from './graphql/queries/getAllCarrierOptions';
import { getCarrierName } from './utils/carrierUtils';

const PageBoy: React.FC = () => {
    const { currentWarehouse } = useWarehouseContext();
    const [selectedBagNumber, setSelectedBagNumber] = useState<string | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [newBagUnifiedVisible, setNewBagUnifiedVisible] = useState(false);
    const [printTagsModalVisible, setPrintTagsModalVisible] = useState(false);
    const [printTTTagsModalVisible, setPrintTTTagsModalVisible] = useState(false);
    const [bagsToPrint, setBagsToPrint] = useState<TTBag[]>([]);
    const [ttBagsToPrint, setTTBagsToPrint] = useState<TTBag[]>([]);
    const [printCopies, setPrintCopies] = useState<number>(1);
    const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    // TaskDetailDrawer 相关状态
    const [taskDetailVisible, setTaskDetailVisible] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    
    const warehouse = currentWarehouse!!;
    
    const BagInitialFilters: BagFiltersType = {
        warehouseId: warehouse?.id || '0'
    };
    const [filters, setFilters] = useState<BagFiltersType>(BagInitialFilters);
    const { t, i18n } = useTranslation();
    const [tempFilterOptions, setTempFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);

    // 导出相关状态
    const [exportLoading, setExportLoading] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadFileName, setDownloadFileName] = useState('');
    const [exportCooldown, setExportCooldown] = useState(false);

    const {
        bagData,
        loading,
        refetch,
        totalElements
    } = useBagQuery(filters, warehouse?.id || null, currentPage, pageSize);

    const { data: carrierData } = useQuery<GetAllCarrierOptionsResponse>(GET_ALL_CARRIER_OPTIONS);
    const carrierOptions = carrierData?.getAllCarrierOptions || [];

    const [exportBagTags] = useMutation<ExportBagTagsResponse, ExportBagTagsVariables>(EXPORT_BAG_TAGS, {
        onCompleted(data) {
            const fileName = data.exportBagTags;
            setDownloadFileName(fileName);
            setShowDownloadModal(true);
            setExportLoading(false);
            setExportCooldown(true);
            setTimeout(() => setExportCooldown(false), 5000);
        },
        onError(error) {
            messageApi.error(`${t('wms.bags.messages.failedToDownloadExcel')}: ${error.message}`);
            setExportLoading(false);
        }
    });

    useEffect(() => {
        if (warehouse?.id) {
            refetch({ warehouseId: warehouse.id});
        }
    }, [warehouse?.id, refetch]);

    const handleFilter = (filters: FilterOptions) => {
        setFilterOptions(filters);
        let filteredFilters: BagFiltersType = {
            warehouseId: warehouse.id || '0'
        };
        if (filters.bagNumber) {
            filteredFilters.bagNumber = filters.bagNumber
        }
        if (filters.externalBagNumber) {
            filteredFilters.externalBagNumber = filters.externalBagNumber
        }
        if (filters.bagType && filters.bagType !== 'all') {
            filteredFilters.bagType = Number(filters.bagType)
        }
        if (filters.sortingCode) {
            filteredFilters.sortingCode = filters.sortingCode
        }
        if (filters.status && filters.status !== 'all') {
            filteredFilters.status = Number(filters.status)
        }
        if (filters.createdAtRange[0] !== null && filters.createdAtRange[1] !== null) {
            filteredFilters.createdAt = {
                start: filters.createdAtRange[0].startOf('day').toISOString(),
                end: filters.createdAtRange[1].endOf('day').toISOString(),
            }
        }
        if (filters.bagTaskNumber) {
            filteredFilters.bagTaskNumber = filters.bagTaskNumber
        }
        if (filters.carrierCode !== undefined) {
            filteredFilters.carrierCode = filters.carrierCode
        }
        if (filters.sourceType !== undefined && filters.sourceType !== 'all') {
            const st = Number(filters.sourceType);
            if (Number.isFinite(st)) {
                filteredFilters.sourceType = st;
            }
        }
        if (filters.batchNumber) {
            filteredFilters.batchNumber = filters.batchNumber
        }
        if (filters.mergedToBagNumber) {
            filteredFilters.mergedToBagNumber = filters.mergedToBagNumber
        }
        if (filters.sortingRuleSecond) {
            filteredFilters.sortingRuleSecond = filters.sortingRuleSecond
        }
        // 分拣码分段筛选：4 段独立传给后端，后端按 takeWhile 拼接为完整前缀走 LIKE 'X%'
        if (filters.sortingCodeSegment1) {
            filteredFilters.sortingCodeSegment1 = filters.sortingCodeSegment1
        }
        if (filters.sortingCodeSegment2) {
            filteredFilters.sortingCodeSegment2 = filters.sortingCodeSegment2
        }
        if (filters.sortingCodeSegment3) {
            filteredFilters.sortingCodeSegment3 = filters.sortingCodeSegment3
        }
        if (filters.sortingCodeSegment4) {
            filteredFilters.sortingCodeSegment4 = filters.sortingCodeSegment4
        }
        // PPMOD 筛选：外部业务类型（0=普通 / 1=PP）+ 容器类型 id
        if (filters.externalBusinessType !== undefined) {
            filteredFilters.externalBusinessType = filters.externalBusinessType;
        }
        if (filters.bagContainerId !== undefined) {
            filteredFilters.bagContainerId = filters.bagContainerId;
        }
        setFilters(filteredFilters);
        setCurrentPage(1);
    };

    const [discardSelectedTags, { loading: discarding }] = useDiscardSelectedTags();
    const [reopenSelectedTags, { loading: reopening }] = useReopenSelectedTags();

    // PPMOD 容器候选（行级下拉）；bag_container 字典 ≤ 10 条，首次加载即缓存
    const { data: containerData } = useQuery<ListActiveBagContainersResponse>(
        LIST_ACTIVE_BAG_CONTAINERS,
        { variables: { operateType: BagContainerOperateType.TEMU } }
    );
    const containerCandidates = containerData?.listActiveBagContainers || [];
    const containerSelectOptions = useMemo(
        () => containerCandidates.map(c => ({ label: c.name, value: c.id })),
        [containerCandidates]
    );

    const [updateBagContainer, { loading: updatingContainer }] = useMutation<
        UpdateBagContainerResponse,
        UpdateBagContainerInput
    >(UPDATE_BAG_CONTAINER);

    /**
     * PPMOD 行级编辑：包牌绑定的容器可修改；关包后禁改（后端返回 errorCode 前端友好提示）。
     * 只有 bagContainerId 非 null（即当前已绑定容器）的行才展示 Select；未绑定行显示 "-"（对齐 PRD "如果容器类型不为 null，可以进行行级别修改"）
     */
    const renderContainerCell = (bag: BagType) => {
        // 非 PP 或未绑定容器：只展示（不允许行级选择空 → PP）
        if (bag.externalBusinessType !== ExternalBusinessType.PPMOD || bag.bagContainerId == null) {
            return <span style={{ whiteSpace: 'nowrap' }}>{bag.containerName || '-'}</span>;
        }
        // 关包后 / 已交接 / 已废弃：禁改
        const locked = bag.status === BagStatus.CLOSED
            || bag.status === BagStatus.HANDOVER
            || bag.status === BagStatus.DISCARDED;
        if (locked) {
            return <span style={{ whiteSpace: 'nowrap' }}>{bag.containerName || '-'}</span>;
        }
        return (
            <Select
                size="small"
                style={{ minWidth: 200 }}
                value={bag.bagContainerId}
                options={containerSelectOptions}
                loading={updatingContainer}
                onChange={async (newId: number) => {
                    if (newId === bag.bagContainerId) return;
                    // 更换容器类型前二次确认；用户取消则不发起请求
                    const newName = containerCandidates.find(c => c.id === newId)?.name || '-';
                    const confirmed = await new Promise<boolean>(resolve => {
                        Modal.confirm({
                            title: t('wms.bags.messages.changeContainerConfirmTitle'),
                            content: t('wms.bags.messages.changeContainerConfirmContent', {
                                bagNumber: bag.bagNumber || '-',
                                containerName: newName,
                            }),
                            okText: t('common.confirm'),
                            cancelText: t('common.cancel'),
                            onOk: () => resolve(true),
                            onCancel: () => resolve(false),
                        });
                    });
                    if (!confirmed) return;
                    try {
                        const res = await updateBagContainer({
                            variables: { bagId: bag.id!, bagContainerId: newId },
                        });
                        const payload = res.data?.updateBagContainer;
                        if (!payload?.success) {
                            // 后端 errorCode 直接是完整 i18n key（如 "errors.business.bag.ppBagContainerLockedAfterClose"）；
                            // 优先取 .message 子键，缺失时用 errorMessage 原文兜底
                            const key = payload?.errorCode;
                            const msg = (key && t(`${key}.message`, { defaultValue: '' } as any))
                                || payload?.errorMessage
                                || t('wms.bags.messages.containerUpdateFailed');
                            messageApi.error(msg as string);
                            return;
                        }
                        messageApi.success(t('wms.bags.messages.containerUpdatedSuccessfully'));
                        refetch();
                    } catch (e: any) {
                        showError(e);
                    }
                }}
            />
        );
    };

    const bagList = bagData || [];

    const showNewBagModal = () => {
        setNewBagUnifiedVisible(true);
    };

    const handleNewBagSubmitSuccess = (createdBags: TTBag[], copies: number) => {
        setNewBagUnifiedVisible(false);
        setPrintCopies(copies);
        refetch();
        // 根据 brand 路由到对应打印弹窗
        if (createdBags.length > 0 && isTTBusinessType(createdBags[0].businessType)) {
            setTTBagsToPrint(createdBags);
            setPrintTTTagsModalVisible(true);
        } else {
            setBagsToPrint(createdBags);
            setPrintTagsModalVisible(true);
        }
    };



    // Handle print selected tags
    const handlePrintSelected = async () => {
        if (selectedRowKeys.length === 0) {
            messageApi.warning(t('wms.bags.messages.selectTagsToPrint'));
            return;
        }

        const selected = bagList.filter(bag => bag.id && selectedRowKeys.includes(bag.id.toString()));
        const allTT = selected.length > 0 && selected.every(bag => isTTBusinessType(bag.businessType));

        if (allTT) {
            const ttBags: TTBag[] = selected.map(bag => ({
                id: bag.id!.toString(),
                bagNumber: bag.bagNumber,
                bagType: bag.bagType,
                businessType: bag.businessType,
                warehouseId: bag.warehouseId,
                sortingCode: bag.sortingCode,
                fromLocation: bag.fromLocation,
                toLocation: bag.toLocation,
                nextStop: bag.nextStop,
                carrierCode: bag.carrierCode,
                status: bag.status,
                createdAt: bag.createdAt,
            }));
            setTTBagsToPrint(ttBags);
            setPrintTTTagsModalVisible(true);
        } else {
            const bags = selected.map(bag => ({
                id: bag.id!.toString(),
                bagNumber: bag.bagNumber,
                bagType: bag.bagType,
                warehouseId: bag.warehouseId,
                sortingCode: bag.sortingCode,
                status: bag.status,
                createdAt: bag.createdAt,
            }));
            setBagsToPrint(bags);
            setPrintTagsModalVisible(true);
        }
        setSelectedRowKeys([]);
    };

    // Handle discard selected tags
    const handleDiscardSelected = async () => {
        if (selectedRowKeys.length === 0) {
            messageApi.warning(t('wms.bags.messages.selectTagsToDiscard'));
            return;
        }

        // 只有未使用(status=0)的包牌可以作废
        const selectedBags = bagList.filter(bag => selectedRowKeys.includes(bag.id?.toString() || ''));
        const nonUnusedBags = selectedBags.filter(bag => bag.status !== 0); // status = 0 表示未使用

        if (nonUnusedBags.length > 0) {
            const bagNumbers = nonUnusedBags.map(bag => bag.bagNumber).join(', ');
            messageApi.error(t('wms.bags.messages.cannotDiscardNonUnusedTags', { tags: bagNumbers }));
            return;
        }

        try {
            const result = await discardSelectedTags({
                variables: { ids: selectedRowKeys as string[] }
            });

            if (result.data?.discardSelectedTags.success) {
                messageApi.success(t('wms.bags.messages.tagsDiscardedSuccessfully'));
                setSelectedRowKeys([]);
                refetch();
            }
        } catch (error: any) {
            showError(error);
        }
    };

    // Handle reopen selected tags (CLOSED → USED)
    const handleReopenSelected = async () => {
        if (selectedRowKeys.length === 0) {
            messageApi.warning(t('wms.bags.messages.selectTagsToReopen'));
            return;
        }

        // 检查是否有非"已关包"状态的标签
        const selectedBags = bagList.filter(bag => selectedRowKeys.includes(bag.id?.toString() || ''));
        const nonClosedBags = selectedBags.filter(bag => bag.status !== BagStatus.CLOSED);

        if (nonClosedBags.length > 0) {
            const nonClosedBagNumbers = nonClosedBags.map(bag => bag.bagNumber).join(', ');
            messageApi.error(t('wms.bags.messages.cannotReopenNonClosedTags', { tags: nonClosedBagNumbers }));
            return;
        }

        // 只允许打开SWX类型的包牌
        const nonSwxBags = selectedBags.filter(bag => bag.businessType !== BusinessType.SWIFTX);
        if (nonSwxBags.length > 0) {
            messageApi.error(t('wms.bags.messages.cannotReopenNonSwxTags'));
            return;
        }

        try {
            const result = await reopenSelectedTags({
                variables: { ids: selectedRowKeys as string[] }
            });

            if (result.data?.reopenSelectedTags.success) {
                messageApi.success(t('wms.bags.messages.tagsReopenedSuccessfully'));
                setSelectedRowKeys([]);
                refetch();
            } else if (result.data?.reopenSelectedTags.message) {
                messageApi.error(result.data.reopenSelectedTags.message);
            }
        } catch (error: any) {
            showError(error);
        }
    };

    // Handle download - export current table data to Excel
    const handleDownload = async () => {
        try {
            await exportBagTags({
                variables: {
                    filters: filters,
                    language: i18n.language
                }
            });
        } catch (error) {
            console.error('Export error:', error);
        }
    };

    // Row selection configuration
    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
        onSelectAll: (selected: boolean, selectedRows: BagType[], changeRows: BagType[]) => {
            if (selected) {
                const newSelectedKeys = filteredData.map(item => item.id || '');
                setSelectedRowKeys(newSelectedKeys);
            } else {
                setSelectedRowKeys([]);
            }
        },
        onSelect: (record: BagType, selected: boolean) => {
            if (selected) {
                setSelectedRowKeys([...selectedRowKeys, record.id || '']);
            } else {
                setSelectedRowKeys(selectedRowKeys.filter(key => key !== record.id));
            }
        },
    };

    // Table column definitions
    const columns: TableColumnsType<BagType> = [
        {
            title: t('wms.bags.columns.bagNumber'),
            dataIndex: 'bagNumber',
            key: 'bagNumber',
            align: 'left' as const,
            ellipsis: false,
            render: (bagNumber: string) => (
                <Button
                    type="link"
                    style={{ paddingLeft: 0 }}
                    onClick={() => {
                        setSelectedBagNumber(bagNumber);
                        setDrawerVisible(true);
                    }}
                >
                    <span style={{ whiteSpace: 'nowrap' }}>{bagNumber}</span>
                </Button>
            )
        },
        {
            title: t('wms.bags.columns.externalBagNumber'),
            dataIndex: 'externalBagNumber',
            key: 'externalBagNumber',
            align: 'left' as const,
            ellipsis: false,
            render: (val: string | undefined) => <span style={{ whiteSpace: 'nowrap' }}>{val || '-'}</span>
        },
        {
            title: t('wms.bags.columns.mergedTo'),
            dataIndex: 'mergedToBagNumber',
            key: 'mergedToBagNumber',
            align: 'left' as const,
            ellipsis: false,
            render: (mergedToBagNumber: string | undefined) => (
                <span style={{ whiteSpace: 'nowrap' }}>{mergedToBagNumber || '-'}</span>
            )
        },
        {
            title: t('wms.bags.columns.businessType'),
            dataIndex: 'businessType',
            key: 'businessType',
            align: 'left' as const,
            ellipsis: false,
            filters: [
                { text: 'SWX', value: 1 },
                { text: 'TT', value: 2 },
            ],
            onFilter: (value, record) => record.businessType === Number(value),
            render: (businessType: number) => <span style={{ whiteSpace: 'nowrap' }}>{getBusinessTypeText(businessType)}</span>
        },
        {
            title: t('wms.bags.columns.printStyle'),
            dataIndex: 'printType',
            key: 'printType',
            align: 'left' as const,
            ellipsis: false,
            render: (printType: number) => <span style={{ whiteSpace: 'nowrap' }}>{getPrintTypeText(printType)}</span>
        },
        {
            title: t('wms.bags.columns.ttBizType'),
            dataIndex: 'ttBizType',
            key: 'ttBizType',
            align: 'left' as const,
            ellipsis: false,
            render: (ttBizType: string) => <span style={{ whiteSpace: 'nowrap' }}>{ttBizType === 'UNLIMITED' ? t('wms.bags.pageDetails.ttBizTypeUnlimited') : (ttBizType || '-')}</span>
        },
        {
            title: t('wms.bags.columns.sortingSegmentCode'),
            dataIndex: 'sortingSegmentCode',
            key: 'sortingSegmentCode',
            align: 'left' as const,
            ellipsis: false,
            render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>
        },
        {
            title: t('wms.bags.columns.lastMileSortingCode'),
            dataIndex: 'lastMileSortingCode',
            key: 'lastMileSortingCode',
            align: 'left' as const,
            ellipsis: false,
            render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>
        },
        {
            title: t('wms.bags.columns.createdAt'),
            dataIndex: 'warehouseId',
            key: 'warehouseId',
            align: 'left' as const,
            ellipsis: false,
            render: (warehouseId: number) => <span style={{ whiteSpace: 'nowrap' }}>{warehouse?.name || '-'}</span>
        },
        {
            title: t('wms.bags.columns.createTime'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => {
                const formatted = formatTimestampToDateTime(text);
                return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
            }
        },
        {
            title: t('wms.bags.columns.tagType'),
            dataIndex: 'bagType',
            key: 'bagType',
            align: 'left' as const,
            ellipsis: false,
            filters: bagTypeOptions.map(item => ({
                text: item.label,
                value: item.value,
            })),
            onFilter: (value, record) => record.bagType === Number(value),
            render: (bagType: number) => {
                const option = bagTypeOptions.find(opt => Number(opt.value) === bagType);
                const text = option?.label || '-';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('wms.bags.columns.creationType'),
            dataIndex: 'sourceType',
            key: 'sourceType',
            align: 'left' as const,
            ellipsis: false,
            render: (sourceType: number | undefined) => {
                if (sourceType === undefined || sourceType === null) {
                    return getBagSourceTypeText(0, t); // 默认为手动创建
                }
                return getBagSourceTypeText(sourceType, t);
            }
        },
        {
            title: t('wms.bags.columns.baggingTaskNumber'),
            dataIndex: 'bagTaskName',
            key: 'bagTaskName',
            align: 'left' as const,
            ellipsis: false,
            render: (_: string, record: BagType) => {
                if (!record.bagTaskName || !record.bagTaskId) {
                    return <span style={{ whiteSpace: 'nowrap' }}>-</span>;
                }

                // 可点击的链接样式
                return (
                    <Button
                        type="link"
                        style={{ paddingLeft: 0, color: '#722ed1' }}
                        onClick={() => {
                            if (!record.bagTaskId) {
                                messageApi.warning('该包牌未关联任务信息');
                                return;
                            }

                            setSelectedTaskId(record.bagTaskId.toString());
                            setTaskDetailVisible(true);
                        }}
                    >
                        <span style={{ whiteSpace: 'nowrap' }}>{record.bagTaskName}</span>
                    </Button>
                );
            }
        },
        {
            title: t('wms.bags.columns.batchNumber') || '所属批次',
            dataIndex: 'batchNumber',
            key: 'batchNumber',
            render: (batchNumber: string) => batchNumber || '-'
        },
        {
            title: t('wms.bags.columns.carrier'),
            dataIndex: 'carrierCode',
            key: 'carrierCode',
            align: 'left' as const,
            ellipsis: false,
            render: (carrierCode: number) => {
                const text = getCarrierName(carrierCode, carrierOptions);
                return <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>;
            }
        },
        {
            title: t('wms.bags.columns.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            ellipsis: false,
            filters: getBagStatusOptions(t).map(item => ({
                text: item.label,
                value: item.value,
            })),
            onFilter: (value, record) => record.status === Number(value),
            render: (status: number) => {
                const text = getBagStatusText(status, t);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            // 装包重量：后端存 bag.weight_kg（订单累计，不含皮重）+ containerTareWeightKg（容器皮重，未绑定容器为 null）；
            // 页面展示时相加得到"包裹 + 容器"总重量，再转换为 lb（工具已 i18n"lb/磅"）。
            // 未称重（weightKg null/0）时不加皮重，由 formatWeightKgToLb 内部兜底为 '-'。
            title: t('wms.bags.columns.packageWeight'),
            dataIndex: 'weightKg',
            key: 'weightKg',
            align: 'left' as const,
            ellipsis: false,
            render: (weightKg: number | null | undefined, record: BagType) => {
                const orderKg = weightKg ?? 0;
                if (orderKg <= 0) {
                    return <span style={{ whiteSpace: 'nowrap' }}>{formatWeightKgToLb(weightKg)}</span>;
                }
                const total = orderKg + (record.containerTareWeightKg ?? 0);
                return <span style={{ whiteSpace: 'nowrap' }}>{formatWeightKgToLb(total)}</span>;
            },
        },
        {
            // 外部业务类型：普通(NORMAL/0) 视作无外部业务属性，与 null/undefined 同展示 '-'；
            // 仅非普通值（如 PPMOD）才展示具体文案
            title: t('wms.bags.columns.externalBusinessType') || 'External Business Type',
            dataIndex: 'externalBusinessType',
            key: 'externalBusinessType',
            align: 'left' as const,
            ellipsis: false,
            render: (v: number | null | undefined) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {v == null || v === ExternalBusinessType.NORMAL ? '-' : getExternalBusinessTypeText(v, t)}
                </span>
            ),
        },
        {
            // 容器类型：PP + 已绑 + 未关包时可行级编辑；否则展示 containerName，无值展示 '-'
            title: t('wms.bags.columns.containerType') || 'Container Type',
            key: 'bagContainerId',
            align: 'left' as const,
            ellipsis: false,
            render: (_: unknown, record: BagType) => renderContainerCell(record),
        }
    ];

    if (loading) {
        return (
            <Loading spinning={loading} />
        );
    }

    // 前端过滤逻辑：现在废弃状态的包牌也会显示任务号，所以不再排除
    const filteredData = (bagData || []);

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* Filter area */}
            <FilterArea
                filters={tempFilterOptions}
                setFilters={setTempFilterOptions}
            />

            {/* 批量操作区域 */}
            <div className="flex justify-between items-center mb-3 pl-0">
                <div className="flex gap-4">
                    <Button
                        type="primary"
                        onClick={showNewBagModal}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.bags.buttons.newBagTag')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handlePrintSelected}
                        disabled={selectedRowKeys.length === 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.bags.buttons.printSelectedTag')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleDiscardSelected}
                        disabled={selectedRowKeys.length === 0}
                        loading={discarding}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.bags.buttons.discardSelectedTag')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleReopenSelected}
                        disabled={selectedRowKeys.length === 0}
                        loading={reopening}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.bags.buttons.reopenSelectedTag')}</span>
                    </Button>
                </div>
                <div className="flex gap-4">
                    <Button
                        type="primary"
                        onClick={() => handleFilter(tempFilterOptions)}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.bags.filters.filter')}</span>
                    </Button>
                    <Button
                        onClick={handleDownload}
                        className="w-auto min-w-24"
                        type="primary"
                        loading={exportLoading}
                        disabled={exportCooldown}
                    >
                        <span className="font-bold">{t('wms.bags.buttons.download')}</span>
                    </Button>
                    <Button
                        onClick={() => {
                            setTempFilterOptions(DEFAULT_FILTER_OPTIONS);
                            handleFilter(DEFAULT_FILTER_OPTIONS);
                        }}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.bags.filters.reset')}</span>
                    </Button>
                </div>
            </div>

            {/* BagTag table */}
            <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    rowSelection={rowSelection}
                    pagination={false}
                    tableLayout="auto"
                    scroll={{x: 'max-content'}}
                    size="middle"
                 className="mb-4"/>
            </div>

            {/* 分页 */}
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, pageSize) => {
                    setCurrentPage(page);
                    setPageSize(pageSize);
                }}
            />

            <NewBagUnifiedModal
                visible={newBagUnifiedVisible}
                onClose={() => setNewBagUnifiedVisible(false)}
                onSubmitSuccess={handleNewBagSubmitSuccess}
            />
            <PrintTagsModal
                visible={printTagsModalVisible}
                onClose={() => setPrintTagsModalVisible(false)}
                bags={bagsToPrint}
                initialPrintCopies={printCopies}
            />

            {/* TT 包牌打印弹窗 */}
            <PrintTTTagsModal
                visible={printTTTagsModalVisible}
                onClose={() => setPrintTTTagsModalVisible(false)}
                bags={ttBagsToPrint}
                initialPrintCopies={printCopies}
            />

            <BagDetailDrawer
                visible={drawerVisible}
                bagNumber={selectedBagNumber}
                onClose={() => {
                    setDrawerVisible(false);
                    setSelectedBagNumber(null);
                }}
            />

            {/* 任务详情抽屉 */}
            <TaskDetailDrawer
                visible={taskDetailVisible}
                taskId={selectedTaskId}
                type={1}
                onClose={() => {
                    setTaskDetailVisible(false);
                    setSelectedTaskId(null);
                }}
            />

            {/* 下载中心弹窗 */}
            <WmsDownloadModal
                visible={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                fileName={downloadFileName}
            />
        </div>
    );
};

export const BagsPage = () => {
    return <PageBoy />;
};